/**
 * The readings of one scan: each reader's, kept side by side, and each counted against the
 * true score once there is one.
 *
 * Reading the same page by two readers is the point of the Read board, so a reading stays in
 * the list when another is made, and the list is emptied only when another scan is opened.
 * One reading runs at a time: a reader takes a processor and gigabytes of memory. What it
 * has read so far is put on the desk as it grows, and is no reading in the list until it is
 * whole; stopped, it is gone with the run.
 *
 * A count needs the reading and the true score on the server, and an upload is taken by one
 * job and gone with it, so both are sent again for every count. They are counted one after
 * the other, and counted again from nothing when another true score is given.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type {
  CountPayload,
  RecognisePartial,
  RecognisePayload,
  ScoreReader,
} from "@app/shared-types";
import { api } from "../api/client";
import { said } from "./job-poll";
import { type ScanProgress, scanRead } from "./scan-read";
import { parsed } from "./score-window";

/** A reading on the desk: one made, or what is being read so far (id {@link SOFAR}). */
export interface Shown {
  readonly id: number;
  readonly reader: ScoreReader;
  readonly reading: RecognisePartial;
  /** The reading as this browser read it, or why it could not. */
  readonly score: ReturnType<typeof parsed>;
}

/** A reading made. */
export interface Run extends Shown {
  readonly reading: RecognisePayload;
}

/** The id of what is being read so far; every reading made is numbered from 1. */
export const SOFAR = 0;

/** A reading counted, or why it could not be. */
export type Counted = CountPayload | string;

export interface ReadRuns {
  readonly runs: readonly Run[];
  /** The reading on the desk. */
  readonly shown: Shown | null;
  /** Each reading's count against the true score, by the reading's id, once it is counted. */
  readonly counts: Readonly<Record<number, Counted>>;
  /** Who is reading now and how far they have got, or `null` when nobody is. */
  readonly busy: { readonly reader: ScoreReader; readonly progress: ScanProgress } | null;
  /** Why the last reading asked for was not made. */
  readonly error: string | null;
  read(scan: File, reader: ScoreReader): void;
  cancel(): void;
  show(id: number): void;
  /** Forget every reading: another scan was opened. */
  clear(): void;
}

const MUSICXML = "application/vnd.recordare.musicxml+xml";

const NONE: Readonly<Record<number, Counted>> = {};

export function useReadRuns(truth: File | null): ReadRuns {
  const [runs, setRuns] = useState<readonly Run[]>([]);
  const [shownId, setShownId] = useState<number | null>(null);
  const [busy, setBusy] = useState<ReadRuns["busy"]>(null);
  const [sofar, setSofar] = useState<Shown | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The counts belong to the true score they were counted against, and are no counts at all
  // of another: kept with it, so a new one starts from none without a moment of the old.
  const [kept, setKept] = useState<{ truth: File | null; by: Record<number, Counted> }>({
    truth: null,
    by: {},
  });
  const counts = kept.truth === truth ? kept.by : NONE;

  // Each reading asked for gets a number, checked before anything of it touches the page.
  const generation = useRef(0);
  const job = useRef<string | null>(null);
  const made = useRef(0);

  const stop = useCallback((): void => {
    generation.current += 1;
    const running = job.current;
    job.current = null;
    if (running !== null) void api.cancelRecognise(running).catch(() => undefined);
    setBusy(null);
    setSofar(null);
  }, []);

  // Leaving the board stops a reading still going: nothing would ever collect it.
  useEffect(() => stop, [stop]);

  const read = useCallback(
    (scan: File, reader: ScoreReader): void => {
      stop();
      const mine = generation.current;
      const live = (): boolean => generation.current === mine;
      setError(null);
      const told = (progress: ScanProgress): void => setBusy({ reader, progress });
      // The first of it comes onto the desk, as a reading made does; the rest go on where it
      // still is.
      let first = true;
      const grown = (partial: RecognisePartial): void => {
        if (!live()) return;
        setSofar({ id: SOFAR, reader, reading: partial, score: parsed(partial.musicxml) });
        if (first) setShownId(SOFAR);
        first = false;
      };
      scanRead(
        scan,
        reader.name,
        live,
        told,
        (started) => {
          job.current = started;
        },
        grown,
      )
        .then((reading) => {
          if (!live() || reading === null) return;
          made.current += 1;
          const run = { id: made.current, reader, reading, score: parsed(reading.musicxml) };
          setRuns((before) => [...before, run]);
          setShownId(run.id);
        })
        .catch((cause: unknown) => {
          if (live()) setError(`${reader.label} did not read ${scan.name}: ${said(cause)}`);
        })
        .finally(() => {
          if (!live()) return;
          job.current = null;
          setBusy(null);
          setSofar(null);
        });
    },
    [stop],
  );

  const clear = useCallback((): void => {
    stop();
    setRuns([]);
    setShownId(null);
    setError(null);
    setKept({ truth: null, by: {} });
  }, [stop]);

  // The first reading not yet counted against this true score; its answer brings the next.
  useEffect(() => {
    const next = runs.find((run) => !(run.id in counts));
    if (truth === null || next === undefined) return undefined;
    let live = true;
    const counting = async (): Promise<Counted> => {
      const [reading, score] = await Promise.all([
        api.upload(new Blob([next.reading.musicxml], { type: MUSICXML })),
        api.upload(truth),
      ]);
      return api.count(reading.upload, score.upload);
    };
    counting()
      .catch((cause: unknown): Counted => said(cause))
      .then((counted) => {
        if (!live) return;
        setKept((before) => ({
          truth,
          by: { ...(before.truth === truth ? before.by : {}), [next.id]: counted },
        }));
      });
    return () => {
      live = false;
    };
  }, [runs, counts, truth]);

  const shown = shownId === SOFAR ? sofar : (runs.find((run) => run.id === shownId) ?? null);
  return { runs, shown, counts, busy, error, read, cancel: stop, show: setShownId, clear };
}
